import React, { useState, useEffect, useContext } from "react";
import { LGrid, LButton } from "@loffa/gpp-modules";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faEdit, faCopy } from "@fortawesome/free-solid-svg-icons";
import { AddAutoImportConnectionModal } from "./AddAutoImportConnectionModal";
import styles from "./AutoImportConnections.module.css";
import { DeleteConfirmation } from "../../TemplateManagement/TemplateManagementModals/SharedModals/DeleteConfirmationModal";
import { AuthContext } from "../../../../../../context/AuthContext";

function AutoImportConnections() {
  const [data, setData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const authContext = useContext(AuthContext);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/AutoImportConnections`,
          {
            headers: {
              Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
            },
          }
        );
        if (!response.ok) throw new Error("Failed to fetch handler connections.");
        const result = await response.json();
        setData(result);
        setError(null);
      } catch (error) {
        setError("Error loading data: " + error.message);
      }
    };

    fetchData();
  }, [authContext]);

  const refreshData = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/AutoImportConnections`,
        {
          headers: {
            Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
          },
        }
      );
      if (!response.ok) throw new Error("Failed to fetch handler connections.");
      const result = await response.json();
      setData(result);
      setError(null);
    } catch (error) {
      setError("Error loading data: " + error.message);
    }
  };

  const handleDeleteClick = (id) => {
    setItemToDelete(id);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_GATEWAY_BASE}/multitenancy/api/AutoImportConnections/${itemToDelete}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${authContext.authState().accessToken.accessToken}`,
          },
        }
      );

      if (!response.ok) throw new Error("Failed to delete handler connection.");

      setData(prevData => prevData.filter(item => item.id !== itemToDelete));
      setError(null);
    } catch (error) {
      setError("Error deleting connection: " + error.message);
    } finally {
      setItemToDelete(null);
      setIsDeleteModalOpen(false);
    }
  };

  const handleAddButtonClick = () => {
    setSelectedItem(null);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    refreshData();
  };

  const handleSave = (newData) => {
    setIsModalOpen(false);
    refreshData();
  };

  const handleEditClick = (item) => {
    setSelectedItem(item);
    setIsModalOpen(true);
  };

  const handleDuplicateClick = (item) => {
    // Create a copy of the item without the ID
    const duplicatedItem = {
      ...item,
      id: null,
      description: `${item.description}`
    };
    setSelectedItem({ ...duplicatedItem, isDuplicate: true });
    setIsModalOpen(true);
  };

  const getConnectionTypeName = (type) => {
    switch (type) {
      case 1: return "Network";
      case 2: return "FTP";
      case 3: return "Local Directory";
      case 4: return "SFTP"
      default: return "Unknown";
    }
  };

  const columns = [
    {
      field: "actions",
      title: "Actions",
      width: 125,
      cell: (props) => (
        <td className={styles.actionButtons}>
          <button
            className={styles.editButton}
            onClick={() => handleEditClick(props.dataItem)}
            title="Edit"
          >
            <FontAwesomeIcon icon={faEdit} />
          </button>
          <button
            className={styles.duplicateButton}
            onClick={() => handleDuplicateClick(props.dataItem)}
            title="Duplicate"
          >
            <FontAwesomeIcon icon={faCopy} />
          </button>
          <button
            className={styles.deleteButton}
            onClick={() => handleDeleteClick(props.dataItem?.id)}
            title="Delete"
          >
            <FontAwesomeIcon icon={faTrash} />
          </button>
        </td>
      ),
      filterable: false,
    },
    {
      field: "description",
      title: "Description",
      width: 200
    },
    {
      field: "connectionTypeEnum",
      title: "Connection Type",
      width: 150,
      cell: (props) => (
        <td>{getConnectionTypeName(props.dataItem?.connectionTypeEnum)}</td>
      )
    },
    {
      field: "server",
      title: "Server",
      width: 200
    },
    {
      field: "port",
      title: "Port",
      width: 150,
      cell: (props) => (
        <td>{props.dataItem?.port != null ? props.dataItem?.port : "-"}</td>
      )
    },
    {
      field: "rootPath",
      title: "Root Path",
      width: 200
    },
    {
      field: "createdBy",
      title: "Created By",
      width: 150,
      cell: (props) => (
        <td>{props.dataItem?.createdBy}</td>
      )
    }
  ];

  return (
    <div className={styles.AutoImportConnectionsContainer}>
      <div className={styles.header}>
        <h2>Handler Connections</h2>
        <LButton
          onClick={handleAddButtonClick}
          themeColor="primary"
          className="green"
        >
          <FontAwesomeIcon icon={faPlus} className={styles.buttonIcon} />
          Add Connection
        </LButton>
      </div>

      {error && (
        <div className={styles.error}>{error}</div>
      )}

      <LGrid
        data={data}
        columns={columns}
        sortable
        filterable
        resizable
        pageable
        style={{ height: "400px" }}
        className={styles.grid}
      />

      <AddAutoImportConnectionModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSave={handleSave}
        initialData={selectedItem}
      />

      <DeleteConfirmation
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this connection? This action cannot be undone."
      />
    </div>
  );
}

export { AutoImportConnections };